import {
  completionScoreBarColor,
  DifficultySummaries,
  INACTIVE_BAR_COLOR,
  QuestionDifficulty
} from '@domain/pom'
import {GaugeBar} from '@hconnect/uikit/src/lib2'
import {
  Button,
  DetailsCard,
  H3,
  Loading,
  TextSmallRegular,
  TextSmallSemiBold
} from '@hm/fundamental'
import {mdiDeleteOutline} from '@mdi/js'
import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

interface HierarchyNodeCardProps {
  nodeId: string
  nodeName: string
  difficultySummaries?: DifficultySummaries
  isLoading?: boolean
  onRemove?: (nodeId: string) => void
}

export const HierarchyNodeCard: FC<HierarchyNodeCardProps> = ({
  nodeId,
  nodeName,
  difficultySummaries,
  isLoading = false,
  onRemove
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const isBarActive = (difficulty: QuestionDifficulty) =>
    difficultySummaries &&
    completionScoreBarColor(difficultySummaries, difficulty) !== INACTIVE_BAR_COLOR

  const CounterText = (difficulty: QuestionDifficulty, children: React.ReactNode) =>
    isBarActive(difficulty) ? (
      <TextSmallSemiBold className="text-foreground">{children}</TextSmallSemiBold>
    ) : (
      <TextSmallSemiBold className="text-foreground_faded">{children}</TextSmallSemiBold>
    )

  return (
    <DetailsCard className="p-4" data-cy={`hierarchy-node-card-${nodeId}`}>
      <div className="flex items-center justify-between mb-4">
        <H3 className="text-foreground">{nodeName}</H3>
        {onRemove && (
          <Button
            icon={mdiDeleteOutline}
            variant="tertiary"
            colorClass="text-negative"
            onClick={() => onRemove(nodeId)}
            data-cy={`remove-node-${nodeId}`}
          />
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-9">
          <Loading size="large" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {difficultySummaries && (
            <>
              <GaugeBar
                value={difficultySummaries.basic.answeredPositively}
                maxValue={difficultySummaries.basic.questionCount}
                color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Basic)}
                barHeight={24}
                minLabelWidth={40}
                headerTemplate={
                  <div className="flex justify-between">
                    <TextSmallRegular className="text-foreground_faded">
                      {t('comparativeAnalysis.card.basic')}
                    </TextSmallRegular>
                    {CounterText(
                      QuestionDifficulty.Basic,
                      `${difficultySummaries.basic.answeredPositively}/${difficultySummaries.basic.questionCount}`
                    )}
                  </div>
                }
                customSubtitle={<></>}
                data-test-id={`node-${nodeId}-basic`}
              />
              <GaugeBar
                value={difficultySummaries.advanced.answeredPositively}
                maxValue={difficultySummaries.advanced.questionCount}
                color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Advanced)}
                barHeight={24}
                minLabelWidth={40}
                headerTemplate={
                  <div className="flex justify-between">
                    <TextSmallRegular className="text-foreground_faded">
                      {t('comparativeAnalysis.card.advanced')}
                    </TextSmallRegular>
                    {CounterText(
                      QuestionDifficulty.Advanced,
                      `${difficultySummaries.advanced.answeredPositively}/${difficultySummaries.advanced.questionCount}`
                    )}
                  </div>
                }
                customSubtitle={<></>}
                data-test-id={`node-${nodeId}-advanced`}
              />
              <GaugeBar
                value={difficultySummaries.excellence.answeredPositively}
                maxValue={difficultySummaries.excellence.questionCount}
                color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Excellence)}
                barHeight={24}
                minLabelWidth={40}
                headerTemplate={
                  <div className="flex justify-between">
                    <TextSmallRegular className="text-foreground_faded">
                      {t('comparativeAnalysis.card.excellence')}
                    </TextSmallRegular>
                    {CounterText(
                      QuestionDifficulty.Excellence,
                      `${difficultySummaries.excellence.answeredPositively}/${difficultySummaries.excellence.questionCount}`
                    )}
                  </div>
                }
                customSubtitle={<></>}
                data-test-id={`node-${nodeId}-excellence`}
              />
            </>
          )}
        </div>
      )}
    </DetailsCard>
  )
}
